import { Stack, styled, Switch, SwitchProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomTextView } from "../../../component/CustomTextView";
import { CustomSwitchView } from "../../../component/CustomSwitchView";
import WheelPicker, { PickerData } from "react-simple-wheel-picker";
import {
  COLOR_GREEN,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
} from "../../../../data/colors/Colors";
import { CustomCardView } from "../../../component/CustomCardView";
import { useEffect, useState } from "react";
import { Difficulty } from "../../../../domain/model/Question";

export function SettingsView({
  shouldPlaySounds,
  onShouldPlaySoundsChanged,
  canAnyTeamChooseAnyQuestion,
  onCanAnyTeamChooseAnyQuestionChanged,
  difficultiesMap,
  handleDifficultiesTimeChanged,
}: {
  shouldPlaySounds: boolean
  onShouldPlaySoundsChanged: (can: boolean) => void;
  canAnyTeamChooseAnyQuestion: boolean;
  onCanAnyTeamChooseAnyQuestionChanged: (can: boolean) => void;
  difficultiesMap: Map<Difficulty, number>;
  handleDifficultiesTimeChanged: (
    difficulty: Difficulty,
    value: number
  ) => void;
}) {
  const { t } = useTranslation();

  const [data, setData] = useState<Array<PickerData>>([{ id: "0", value: 0 }]);
  useEffect(() => {
    const d: Array<PickerData> = [];
    for (var i = 20; i <= 150; i++) {
      if (i % 10 != 0) continue;
      d.push({
        id: i.toString(),
        value: i,
      });
    }
    setData(d);
  }, []);

  return (
    <Stack
      spacing={2}
      width={"100%"}
      height={"100%"}
      sx={{ overflow: "auto" }}
      alignItems={"center"}
      justifyContent={"space-between"}
      p={2}
    >
      <Stack alignItems={"center"} width={"100%"} spacing={1}>
        <CustomTextView
          text={t("play_sounds")}
          variant="body1"
          fontWeight="bold"
          textAlign="center"
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          sx={{ direction: "ltr" }}
        >
          <CustomTextView
            text={t("no")}
            variant="h6"
            textColor="red"
            fontWeight="bold"
          />
          <CustomSwitchView
            sx={{ transform: "scale(1.5)" }}
            checked={shouldPlaySounds}
            onChange={(e, checked) =>
              onShouldPlaySoundsChanged(checked)
            }
          />
          <CustomTextView
            text={t("yes")}
            variant="h6"
            textColor="green"
            fontWeight="bold"
          />
        </Stack>
      </Stack>
      <Stack alignItems={"center"} width={"100%"} spacing={1}>
        <CustomTextView
          text={t("can_any_team_choose_any_question")}
          variant="body1"
          fontWeight="bold"
          textAlign="center"
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          sx={{ direction: "ltr" }}
        >
          <CustomTextView
            text={t("no")}
            variant="h6"
            textColor="red"
            fontWeight="bold"
          />
          <CustomSwitchView
            sx={{ transform: "scale(1.5)" }}
            checked={canAnyTeamChooseAnyQuestion}
            onChange={(e, checked) =>
              onCanAnyTeamChooseAnyQuestionChanged(checked)
            }
          />
          <CustomTextView
            text={t("yes")}
            variant="h6"
            textColor="green"
            fontWeight="bold"
          />
        </Stack>
      </Stack>

      <Stack alignItems={"center"} width={"100%"} spacing={2}>
        <CustomTextView
          text={t("difficulties_time")}
          variant="h6"
          fontWeight="bold"
        />

        {data.length > 1 && (
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
            spacing={1}
            sx={{ direction: "ltr" }}
          >
            <DifficultyWheelPicker
              data={data}
              selectedIndex={
                data.length > 1
                  ? data.findIndex(
                      (d) => d.value === difficultiesMap.get(Difficulty.EASY)
                    )
                  : 0
              }
              onDataChanged={(data) => {
                handleDifficultiesTimeChanged(
                  Difficulty.EASY,
                  Number(data.value)
                );
              }}
              color={COLOR_GREEN}
              bgColor="rgba(86, 232, 23, 0.2)"
              title={t("easy")}
            />
            <DifficultyWheelPicker
              data={data}
              selectedIndex={
                data.length > 1
                  ? data.findIndex(
                      (d) => d.value === difficultiesMap.get(Difficulty.MEDIUM)
                    )
                  : 0
              }
              onDataChanged={(data) => {
                handleDifficultiesTimeChanged(
                  Difficulty.MEDIUM,
                  Number(data.value)
                );
              }}
              color="#FFBF00"
              bgColor="rgba(255, 191, 0,0.2)"
              title={t("medium")}
            />

            <DifficultyWheelPicker
              data={data}
              selectedIndex={
                data.length > 1
                  ? data.findIndex(
                      (d) => d.value === difficultiesMap.get(Difficulty.HARD)
                    )
                  : 0
              }
              onDataChanged={(data) => {
                handleDifficultiesTimeChanged(
                  Difficulty.HARD,
                  Number(data.value)
                );
              }}
              color="#f44336"
              bgColor="rgba(244,67,54,0.2)"
              title={t("hard")}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

function DifficultyWheelPicker({
  color,
  bgColor,
  title,
  data,
  onDataChanged,
  selectedIndex,
}: {
  color: string;
  bgColor: string;
  title: string;
  data: PickerData[];
  onDataChanged: (data: PickerData) => void;
  selectedIndex: number;
}) {
  const { t } = useTranslation();
  return (
    <CustomCardView
      background={bgColor}
      padding={2}
      width={"100%"}
      borderRadius={4}
      children={
        <Stack alignItems={"center"} spacing={2}>
          <CustomTextView
            text={title}
            variant="h5"
            fontWeight="bold"
            textColor={color}
          />
          <WheelPicker
            data={data}
            onChange={onDataChanged}
            height={80}
            width={100}
            titleText=""
            itemHeight={26}
            selectedID={data[selectedIndex].id}
            color="#000"
            activeColor={color}
            backgroundColor={"transparent"}
            fontSize={18}
            shadowColor={"transparent"}
          />
          <CustomTextView
            text={t("seconds")}
            variant="subtitle2"
            textColor={COLOR_LIGHT_GRAY}
          />
        </Stack>
      }
    />
  );
}
